import { useEffect, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  SimpleGrid,
  Stack,
  Text,
  useClipboard,
  useToast,
  Wrap,
} from "@chakra-ui/react";
import { formatarDataHora, parseDataISO } from "../../../shared/lib/date";
import { isSuperAdmin } from "../../auth/lib/roles";
import { PLAN_LABEL } from "../../assinatura/lib/plan";
import { adminApi } from "../api/adminApi";
import { useAdminUserQuery } from "../hooks/useAdminUserQuery";
import { useAdminUserActionMutation } from "../hooks/useAdminUserActionMutation";
import { useUpdateUserStatusMutation } from "../hooks/useUpdateUserStatusMutation";
import ConfirmActionDialog from "./ConfirmActionDialog";
import UserAccessModal from "./UserAccessModal";
import { AccessBadge, AccountStatusBadge, EmailBadge, ROLE_LABEL, RoleBadge, formatarData } from "./UserBadges";

const errorMessage = (error) =>
  error.response?.data?.erros?.[0]?.mensagem ?? error.response?.data?.mensagem ?? "Tente de novo em instantes.";

const formatarAcesso = (iso) => (iso ? formatarDataHora(parseDataISO(iso)) : "Nunca entrou");

function Info({ label, children }) {
  return (
    <Box>
      <Text fontSize="xs" color="app-muted-fg">
        {label}
      </Text>
      <Text fontSize="sm" fontWeight="medium">
        {children}
      </Text>
    </Box>
  );
}

function Section({ title, children }) {
  return (
    <Stack spacing={3}>
      <Text fontSize="xs" fontWeight="bold" color="app-muted-fg" textTransform="uppercase" letterSpacing="wide">
        {title}
      </Text>
      {children}
    </Stack>
  );
}

function ResetLinkBox({ link, expiresAt, emailVerified }) {
  const { onCopy, hasCopied } = useClipboard(link);
  return (
    <Stack spacing={2} bg="app-muted" borderRadius="md" p={3}>
      <InputGroup size="sm">
        <Input value={link} isReadOnly pr="5.5rem" aria-label="Link de redefinição de senha" bg="app-card" />
        <InputRightElement width="5.5rem">
          <Button size="xs" onClick={onCopy}>
            {hasCopied ? "Copiado" : "Copiar"}
          </Button>
        </InputRightElement>
      </InputGroup>
      <Text fontSize="xs" color="app-muted-fg">
        Válido até {formatarDataHora(parseDataISO(expiresAt))}. Mande só para o médico: quem tiver o link troca a senha
        da conta. Gerar outro link invalida este.
      </Text>
      {!emailVerified ? (
        <Text fontSize="xs" color="warning-text-strong">
          O e-mail desta conta ainda não foi verificado: marque como verificado para o médico conseguir entrar.
        </Text>
      ) : null}
    </Stack>
  );
}

// Detalhe de uma conta com as ações do admin. Mostra só dados da conta e as CONTAGENS de
// vínculos e plantões, nunca valores. Os botões refletem as regras do backend
// (domain/account_management.go), que continua sendo quem decide.
export default function UserDetailDrawer({ row, viewer, onClose }) {
  const toast = useToast();
  const { data: user = row } = useAdminUserQuery(row?.id, row);
  const updateStatus = useUpdateUserStatusMutation();
  const verifyEmail = useAdminUserActionMutation(adminApi.verifyUserEmail);
  const resendVerification = useAdminUserActionMutation(adminApi.resendVerificationEmail);
  const resetLink = useAdminUserActionMutation(adminApi.createPasswordResetLink);
  const updateRole = useAdminUserActionMutation(({ id, role }) => adminApi.updateUserRole(id, role));

  const [confirming, setConfirming] = useState(null); // deactivate | resetLink | role
  const [generatedLink, setGeneratedLink] = useState(null);
  const [roleDraft, setRoleDraft] = useState("");
  const [accessOpen, setAccessOpen] = useState(false);

  useEffect(() => {
    setGeneratedLink(null);
    setConfirming(null);
    setRoleDraft(row?.role ?? "");
  }, [row?.id, row?.role]);

  if (!row) return null;

  const isSelf = viewer?.id === user.id;
  const isProtected = user.role === "superadmin" && !isSuperAdmin(viewer);
  const canChangeRole = isSuperAdmin(viewer) && !isSelf;

  const run = (mutation, variables, successTitle, onSuccess) =>
    mutation.mutate(variables, {
      onSuccess: (data) => {
        toast({ title: successTitle, status: "success", duration: 4000 });
        setConfirming(null);
        onSuccess?.(data);
      },
      onError: (error) => {
        setConfirming(null);
        toast({ title: "Não foi possível concluir", description: errorMessage(error), status: "error", duration: 6000, isClosable: true });
      },
    });

  const confirmations = {
    deactivate: {
      title: "Desativar conta",
      confirmLabel: "Desativar",
      isDestructive: true,
      isLoading: updateStatus.isPending,
      onConfirm: () => run(updateStatus, { id: user.id, ativo: false }, "Conta desativada"),
      body: "O médico sai na hora de todas as sessões e não consegue mais entrar. Os dados dele continuam guardados e a conta pode ser reativada.",
    },
    resetLink: {
      title: "Gerar link de redefinição de senha",
      confirmLabel: "Gerar link",
      isLoading: resetLink.isPending,
      onConfirm: () => run(resetLink, user.id, "Link gerado", setGeneratedLink),
      body: "O link vale por 24 horas e permite trocar a senha desta conta. Um link gerado antes deixa de valer.",
    },
    role: {
      title: "Alterar perfil",
      confirmLabel: "Alterar perfil",
      isDestructive: (roleDraft === "user" || (user.role === "superadmin" && roleDraft === "admin")),
      isLoading: updateRole.isPending,
      onConfirm: () => run(updateRole, { id: user.id, role: roleDraft }, "Perfil atualizado"),
      body: `${user.email} passa de ${ROLE_LABEL[user.role]} para ${ROLE_LABEL[roleDraft]}. Ao rebaixar, as sessões abertas da conta são encerradas.`,
    },
  };
  const confirmation = confirming ? confirmations[confirming] : null;

  return (
    <>
      <Drawer isOpen onClose={onClose} placement="right" size="md">
        <DrawerOverlay />
        <DrawerContent bg="app-card">
          <DrawerCloseButton aria-label="Fechar" />
          <DrawerHeader pr={12}>{user.nome || user.email}</DrawerHeader>
          <DrawerBody pb={8}>
            <Stack spacing={6}>
              <Stack spacing={2}>
                <Text fontSize="sm" color="app-muted-fg">
                  {user.email}
                </Text>
                <Wrap spacing={2}>
                  <RoleBadge role={user.role} />
                  <AccountStatusBadge ativo={user.ativo} />
                  <EmailBadge verified={user.is_email_verified} />
                  <AccessBadge row={user} />
                </Wrap>
              </Stack>

              {isProtected ? (
                <Alert status="info" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  <AlertDescription>Só um superadministrador altera esta conta.</AlertDescription>
                </Alert>
              ) : null}

              <Section title="Conta">
                <SimpleGrid columns={2} spacing={3}>
                  <Info label="Cadastro">{user.created_at}</Info>
                  <Info label="Último acesso">{formatarAcesso(user.last_login_at)}</Info>
                  <Info label="Plano">{PLAN_LABEL[user.plan] ?? user.plan}</Info>
                  <Info label="Teste até">{formatarData(user.trial_ate)}</Info>
                  <Info label="Assinatura até">{user.assinatura_ate ? formatarData(user.assinatura_ate) : "Nunca assinou"}</Info>
                  <Info label="Login com Google">{user.google_linked === undefined ? "—" : user.google_linked ? "Vinculado" : "Não"}</Info>
                </SimpleGrid>
              </Section>

              <Section title="Uso">
                <SimpleGrid columns={2} spacing={3}>
                  <Info label="Vínculos">{user.vinculos ?? "—"}</Info>
                  <Info label="Plantões">{user.plantoes ?? "—"}</Info>
                </SimpleGrid>
              </Section>

              <Divider />

              {!user.is_email_verified ? (
                <Section title="E-mail não verificado">
                  <Text fontSize="sm" color="app-muted-fg">
                    Sem o e-mail verificado o médico não consegue entrar com senha.
                  </Text>
                  <Wrap spacing={2}>
                    <Button
                      size="sm"
                      isDisabled={isProtected}
                      isLoading={verifyEmail.isPending}
                      onClick={() => run(verifyEmail, user.id, "E-mail marcado como verificado")}
                    >
                      Marcar e-mail como verificado
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      isDisabled={isProtected}
                      isLoading={resendVerification.isPending}
                      onClick={() => run(resendVerification, user.id, "E-mail de verificação reenviado")}
                    >
                      Reenviar e-mail de verificação
                    </Button>
                  </Wrap>
                </Section>
              ) : null}

              <Section title="Acesso à conta">
                <Wrap spacing={2}>
                  <Button size="sm" variant="outline" isDisabled={isProtected} onClick={() => setConfirming("resetLink")}>
                    Gerar link de redefinição de senha
                  </Button>
                  <Button size="sm" variant="outline" isDisabled={isProtected} onClick={() => setAccessOpen(true)}>
                    Liberar acesso
                  </Button>
                  {user.ativo ? (
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="danger"
                      isDisabled={isSelf || isProtected}
                      onClick={() => setConfirming("deactivate")}
                    >
                      Desativar conta
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="success"
                      isDisabled={isProtected}
                      isLoading={updateStatus.isPending}
                      onClick={() => run(updateStatus, { id: user.id, ativo: true }, "Conta ativada")}
                    >
                      Ativar conta
                    </Button>
                  )}
                </Wrap>
                {generatedLink ? (
                  <ResetLinkBox link={generatedLink.link} expiresAt={generatedLink.expires_at} emailVerified={user.is_email_verified} />
                ) : null}
              </Section>

              {canChangeRole ? (
                <Section title="Perfil">
                  <HStack spacing={2} align="flex-end">
                    <Select size="sm" aria-label="Perfil" value={roleDraft} onChange={(e) => setRoleDraft(e.target.value)}>
                      {Object.entries(ROLE_LABEL).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>
                    <Button size="sm" flexShrink={0} isDisabled={roleDraft === user.role} onClick={() => setConfirming("role")}>
                      Alterar perfil
                    </Button>
                  </HStack>
                  <Text fontSize="xs" color="app-muted-fg">
                    Administradores gerenciam contas; superadministradores também alteram perfis.
                  </Text>
                </Section>
              ) : null}
              {isSuperAdmin(viewer) && isSelf ? (
                <Text fontSize="sm" color="app-muted-fg">
                  Você não pode alterar o próprio perfil.
                </Text>
              ) : null}
            </Stack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {confirmation ? (
        <ConfirmActionDialog
          isOpen
          onClose={() => setConfirming(null)}
          onConfirm={confirmation.onConfirm}
          title={confirmation.title}
          confirmLabel={confirmation.confirmLabel}
          isLoading={confirmation.isLoading}
          isDestructive={confirmation.isDestructive}
        >
          <Text fontSize="sm">{confirmation.body}</Text>
        </ConfirmActionDialog>
      ) : null}

      <UserAccessModal user={accessOpen ? user : null} onClose={() => setAccessOpen(false)} />
    </>
  );
}
